import React from "react";
import {
  ReactFlow, ReactFlowProvider, Background, BackgroundVariant, Controls, MiniMap,
  Handle, Position, useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import toast from "react-hot-toast";
import {
  Network, Search, ChevronDown, ChevronRight, Crown, Maximize2, Minimize2,
  Users, Mail, Building2, Briefcase, Locate, X,
} from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";
import { useApi } from "../../../hooks/useApi";
import { useAuth } from "../../../contexts/AuthContext";
import { PageShell } from "../components/PageShell";
import { Card, Button, Badge, Avatar } from "../components/primitives";
import { EmptyState } from "../components/DataTable";
import { Drawer } from "../components/Drawer";
import { layoutTree, indexTree, NODE_W, NODE_H } from "../utils/orgChartLayout.mjs";

// Expanding everything in a 5000-person org would mount 5000 cards at once. The
// canvas culls off-screen nodes, but the layout pass and React reconciliation
// still walk the full set, so cap it and tell the user why.
// ponytail: hard cap; if orgs routinely need more, expand-all becomes a
// depth-limited "expand N levels" instead.
const EXPAND_ALL_CAP = 1500;

function OrgCard({ data, selected }) {
  const t = useDashTheme();
  const { node, open, onToggle, onOpen, isMatch } = data;
  const accent = node.level != null ? t.accents[node.level % t.accents.length] : t.c.textTertiary;
  const hasReports = node.reports.length > 0;

  return (
    <div style={{ width: NODE_W, height: NODE_H, position: "relative" }}>
      <Handle type="target" position={Position.Top} style={{ opacity: 0, pointerEvents: "none" }} />
      <div
        onClick={() => onOpen(node)}
        style={{
          width: "100%", height: "100%", display: "flex", alignItems: "center", gap: 9,
          padding: "0 10px", boxSizing: "border-box", cursor: "pointer",
          background: t.c.surface,
          border: `1px solid ${selected || isMatch ? t.c.primary : t.c.border}`,
          borderLeft: `3px solid ${accent}`,
          borderRadius: t.r.lg,
          boxShadow: selected || isMatch ? `0 0 0 3px ${t.c.primarySoft}, ${t.shadow.md}` : t.shadow.sm,
          transition: "box-shadow 140ms ease, border-color 140ms ease",
        }}
      >
        <Avatar name={node.name} src={node.avatar || undefined} size={34} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 5,
            fontSize: t.font.size.md, fontWeight: t.font.weight.semibold, color: t.c.text,
          }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.name}</span>
            {node.role === "org_admin" && <Crown size={12} style={{ color: t.accents[3], flexShrink: 0 }} />}
          </div>
          <div style={{
            fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 2,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {node.position || node.email}
          </div>
          {node.levelName && (
            <div style={{
              display: "inline-flex", alignItems: "center", marginTop: 4, padding: "0 5px",
              borderRadius: t.r.xs, background: `${accent}1A`, color: accent,
              fontSize: 10, fontWeight: t.font.weight.medium, letterSpacing: "0.03em",
              textTransform: "uppercase", maxWidth: "100%", overflow: "hidden",
              textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {node.levelName}
            </div>
          )}
        </div>

        {/* Expand/collapse lives inside the card: a pill straddling the bottom
            edge lands on the card below whenever rows run close or the canvas is
            zoomed out, which read as overlapping cards. The count is the whole
            subtree, so a collapsed manager still tells you how big their org is. */}
        {hasReports && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
            title={open ? "Collapse" : `Expand ${node.count} below`}
            style={{
              flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 3,
              height: 22, padding: "0 7px",
              border: `1px solid ${open ? t.c.border : t.c.primary}`, borderRadius: 99,
              background: open ? t.c.surfaceMuted : t.c.primary,
              color: open ? t.c.textSecondary : "#fff",
              fontSize: 11, fontWeight: t.font.weight.medium, fontFamily: t.font.family,
              cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            {node.count}
          </button>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, pointerEvents: "none" }} />
    </div>
  );
}

// Invisible waypoint that carries a long edge through a row without touching the
// cards on it. Purely a routing anchor — nothing to see, nothing to click.
function OrgJoint() {
  return (
    <div style={{ width: 1, height: 1 }}>
      <Handle type="target" position={Position.Top} style={{ opacity: 0, pointerEvents: "none" }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, pointerEvents: "none" }} />
    </div>
  );
}

const nodeTypes = { orgCard: OrgCard, orgJoint: OrgJoint };

function OrgChartCanvas({ onMeta, controls }) {
  const t = useDashTheme();
  const api = useApi();
  const { user } = useAuth();
  const flow = useReactFlow();

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [expanded, setExpanded] = React.useState(() => new Set());
  const [selected, setSelected] = React.useState(null);
  const [detail, setDetail] = React.useState(null);
  const [query, setQuery] = React.useState("");

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/api/v1/users/hierarchy");
        if (!alive) return;
        const payload = res.data?.data || {};
        setData(payload);
        onMeta?.({ total: payload.totalUsers ?? 0, scope: payload.scope });
        // Open the roots and their direct reports: enough to read the shape of
        // the org without mounting the whole thing.
        const seed = new Set();
        (payload.hierarchy || []).forEach((root) => {
          seed.add(root.id);
          root.reports.forEach((child) => seed.add(child.id));
        });
        setExpanded(seed);
      } catch (_) {
        if (alive) setData({ hierarchy: [], levels: [], totalUsers: 0 });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable identity matters here: `roots` feeds the layout memo, and a fresh
  // array each render would re-lay the whole chart on every keystroke.
  const roots = React.useMemo(() => data?.hierarchy || [], [data]);
  const index = React.useMemo(() => indexTree(roots), [roots]);

  const toggle = React.useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const matches = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const out = [];
    for (const { node } of index.values()) {
      const hay = `${node.name} ${node.position || ""} ${node.email || ""} ${node.department || ""}`.toLowerCase();
      if (hay.includes(q)) out.push(node);
      if (out.length >= 8) break;
    }
    return out;
  }, [query, index]);

  // Reveal a person: open every manager above them, then fly the viewport there.
  const revealNode = React.useCallback((id) => {
    const next = new Set(expanded);
    let cur = index.get(id);
    while (cur?.parentId) { next.add(cur.parentId); cur = index.get(cur.parentId); }
    setExpanded(next);
    setSelected(id);
    setQuery("");
    // The canvas store hasn't seen the expansion yet, so lay the tree out here
    // rather than asking it where the node landed.
    const target = layoutTree(roots, (nodeId) => next.has(nodeId)).nodes.find((n) => n.id === id);
    if (target) {
      flow.setCenter(target.position.x + NODE_W / 2, target.position.y + NODE_H / 2, { zoom: 1, duration: 500 });
    }
  }, [expanded, index, roots, flow]);

  const expandAll = React.useCallback(() => {
    if (index.size > EXPAND_ALL_CAP) {
      toast.error(`Too many people to expand at once (${index.size}). Open branches as you need them.`);
      return;
    }
    setExpanded(new Set(index.keys()));
  }, [index]);

  const collapseAll = React.useCallback(() => {
    setExpanded(new Set(roots.map((r) => r.id)));
  }, [roots]);

  React.useEffect(() => {
    if (controls) controls.current = { expandAll, collapseAll };
  }, [controls, expandAll, collapseAll]);

  const isOpen = React.useCallback((id) => expanded.has(id), [expanded]);

  const { nodes, edges } = React.useMemo(() => {
    const laid = layoutTree(roots, isOpen);
    const matchIds = new Set(matches.map((m) => m.id));
    return {
      nodes: laid.nodes.map((n) => ({
        ...n,
        selected: n.id === selected,
        data: { ...n.data, onToggle: toggle, onOpen: setDetail, isMatch: matchIds.has(n.id) },
      })),
      edges: laid.edges.map((e) => ({
        ...e,
        style: { stroke: t.c.borderStrong, strokeWidth: 2.5 },
      })),
    };
  }, [roots, isOpen, selected, matches, toggle, t.c.borderStrong]);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: t.c.textTertiary, fontSize: t.font.size.sm }}>
        Building the org chart…
      </div>
    );
  }

  if (roots.length === 0) {
    return (
      <Card padding={0}>
        <EmptyState
          icon={<Network size={22} />}
          title="Nothing to chart yet"
          description="Assign positions and reporting managers to your team and the chart will draw itself."
        />
      </Card>
    );
  }

  return (
    <>
      <div style={{ position: "relative", width: "100%", height: "100%", borderRadius: t.r.lg, overflow: "hidden", border: `1px solid ${t.c.border}`, background: t.c.bg }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.05}
          maxZoom={2}
          // Viewport culling: only the cards actually on screen get mounted, which
          // is what makes a wide org survive an expanded branch.
          onlyRenderVisibleElements
          nodesDraggable={false}
          nodesConnectable={false}
          edgesFocusable={false}
          proOptions={{ hideAttribution: true }}
          onPaneClick={() => setSelected(null)}
        >
          <Background variant={BackgroundVariant.Dots} gap={22} size={1} color={t.c.border} />
          <Controls showInteractive={false} />
          <MiniMap
            pannable zoomable
            style={{ background: t.c.surface, border: `1px solid ${t.c.border}`, borderRadius: t.r.md }}
            maskColor={t.isDark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.08)"}
            nodeColor={(n) => (n.data?.node?.level != null ? t.accents[n.data.node.level % t.accents.length] : t.c.borderStrong)}
            nodeStrokeWidth={0}
          />

          {/* Search overlay — jumps to a person and opens the path down to them. */}
          <div style={{ position: "absolute", top: 12, left: 12, zIndex: 5, width: 280 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 8, height: 34, padding: "0 10px",
              background: t.c.surface, border: `1px solid ${t.c.border}`,
              borderRadius: matches.length ? `${t.r.sm}px ${t.r.sm}px 0 0` : t.r.sm,
              boxShadow: t.shadow.sm,
            }}>
              <Search size={14} style={{ color: t.c.textTertiary, flexShrink: 0 }} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Find someone…"
                style={{ flex: 1, border: "none", outline: "none", background: "transparent", color: t.c.text, fontSize: t.font.size.sm, fontFamily: t.font.family }}
              />
              {query && (
                <button onClick={() => setQuery("")} style={{ border: "none", background: "transparent", color: t.c.textTertiary, cursor: "pointer", display: "inline-flex", padding: 0 }}>
                  <X size={13} />
                </button>
              )}
            </div>
            {matches.length > 0 && (
              <div style={{
                background: t.c.surface, border: `1px solid ${t.c.border}`, borderTop: "none",
                borderRadius: `0 0 ${t.r.sm}px ${t.r.sm}px`, boxShadow: t.shadow.md, overflow: "hidden",
              }}>
                {matches.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => revealNode(m.id)}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "7px 10px",
                      border: "none", borderTop: `1px solid ${t.c.border}`, background: "transparent",
                      color: t.c.text, fontFamily: t.font.family, cursor: "pointer", textAlign: "left",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = t.c.surfaceMuted; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <Avatar name={m.name} src={m.avatar || undefined} size={24} />
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ display: "block", fontSize: t.font.size.sm, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name}</span>
                      <span style={{ display: "block", fontSize: t.font.size.xs, color: t.c.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.position || m.email}</span>
                    </span>
                    <Locate size={12} style={{ color: t.c.textTertiary, flexShrink: 0 }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Level legend, so the accent stripe on each card means something. */}
          {(data?.levels || []).length > 0 && (
            <div style={{
              position: "absolute", top: 12, right: 12, zIndex: 5, maxWidth: 200,
              padding: "8px 10px", background: t.c.surface, border: `1px solid ${t.c.border}`,
              borderRadius: t.r.sm, boxShadow: t.shadow.sm,
              display: "flex", flexDirection: "column", gap: 5,
            }}>
              {data.levels.map((name, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: t.font.size.xs, color: t.c.textSecondary }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: t.accents[i % t.accents.length], flexShrink: 0 }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
                </div>
              ))}
            </div>
          )}
        </ReactFlow>
      </div>

      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail?.name || ""}
        subtitle={detail?.position || "Team member"}
        icon={<Users size={18} />}
        width={420}
        footer={<Button variant="primary" onClick={() => setDetail(null)}>Close</Button>}
      >
        {detail && <NodeDetail t={t} node={detail} index={index} onJump={revealNode} currentUserId={user?.id || user?._id} />}
      </Drawer>
    </>
  );
}

function DetailRow({ t, icon, label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: `1px solid ${t.c.border}` }}>
      <span style={{ display: "inline-flex", color: t.c.textTertiary, flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, width: 96, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: t.font.size.sm, color: t.c.text, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{value}</span>
    </div>
  );
}

function NodeDetail({ t, node, index, onJump, currentUserId }) {
  const entry = index.get(node.id);
  const manager = entry?.parentId ? index.get(entry.parentId)?.node : null;
  // Managers beyond the primary one (dual reporting) are only resolvable if they
  // are inside the visible tree; a senior outside the caller's scope stays hidden.
  const alsoReportsTo = (node.managerIds || [])
    .slice(1)
    .map((id) => index.get(id)?.node)
    .filter(Boolean);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: t.s[4] }}>
        <Avatar name={node.name} src={node.avatar || undefined} size={52} highlight={node.id === String(currentUserId)} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: t.font.size.lg, fontWeight: t.font.weight.semibold, color: t.c.text }}>{node.name}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
            {node.levelName && <Badge tone="brand">{node.levelName}</Badge>}
            <Badge tone="neutral">{node.count} below</Badge>
          </div>
        </div>
      </div>

      <DetailRow t={t} icon={<Briefcase size={14} />} label="Position" value={node.position} />
      <DetailRow t={t} icon={<Building2 size={14} />} label="Department" value={node.department} />
      <DetailRow t={t} icon={<Mail size={14} />} label="Email" value={node.email} />
      <DetailRow t={t} icon={<Users size={14} />} label="Direct reports" value={String(node.reports.length)} />

      {manager && (
        <div style={{ marginTop: t.s[5] }}>
          <div style={{ fontSize: t.font.size.xs, fontWeight: t.font.weight.semibold, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: t.s[2] }}>
            Reports to
          </div>
          <PersonRow t={t} person={manager} onJump={onJump} />
          {alsoReportsTo.map((m) => <PersonRow key={m.id} t={t} person={m} onJump={onJump} />)}
        </div>
      )}

      {node.reports.length > 0 && (
        <div style={{ marginTop: t.s[5] }}>
          <div style={{ fontSize: t.font.size.xs, fontWeight: t.font.weight.semibold, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: t.s[2] }}>
            Direct reports · {node.reports.length}
          </div>
          {node.reports.map((r) => <PersonRow key={r.id} t={t} person={r} onJump={onJump} />)}
        </div>
      )}
    </div>
  );
}

function PersonRow({ t, person, onJump }) {
  return (
    <button
      onClick={() => onJump(person.id)}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "7px 8px",
        border: `1px solid ${t.c.border}`, borderRadius: t.r.sm, background: t.c.surface,
        marginBottom: 6, cursor: "pointer", textAlign: "left", fontFamily: t.font.family,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = t.c.surfaceMuted; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = t.c.surface; }}
    >
      <Avatar name={person.name} src={person.avatar || undefined} size={26} />
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: "block", fontSize: t.font.size.sm, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{person.name}</span>
        <span style={{ display: "block", fontSize: t.font.size.xs, color: t.c.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{person.position || person.email}</span>
      </span>
      {person.count > 0 && <Badge tone="neutral">{person.count}</Badge>}
      <Locate size={12} style={{ color: t.c.textTertiary, flexShrink: 0 }} />
    </button>
  );
}

// The header's Expand/Collapse buttons live outside the canvas, so the canvas
// publishes its handlers into a ref the header can call.
export default function OrgChartV2() {
  const [meta, setMeta] = React.useState({ total: null, scope: null });
  const controls = React.useRef({});

  return (
    <PageShell
      eyebrow="Workspace · People"
      title="Org structure"
      subtitle={meta.total != null
        ? `${meta.total} ${meta.total === 1 ? "person" : "people"}${meta.scope === "subtree" ? " in your reporting line" : ""}`
        : "Who reports to who, across the company"}
      icon={<Network size={22} />}
      actions={
        <>
          <Button variant="secondary" leftIcon={<Minimize2 size={15} />} onClick={() => controls.current.collapseAll?.()}>Collapse</Button>
          <Button variant="secondary" leftIcon={<Maximize2 size={15} />} onClick={() => controls.current.expandAll?.()}>Expand all</Button>
        </>
      }
    >
      {/* The canvas needs a bounded height to lay itself out; PageShell's content
          area scrolls, so pin the chart to the remaining viewport instead. */}
      <div style={{ height: "calc(100vh - 190px)", minHeight: 420 }}>
        <ReactFlowProvider>
          <OrgChartCanvas onMeta={setMeta} controls={controls} />
        </ReactFlowProvider>
      </div>
    </PageShell>
  );
}
